import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { twoFactor, useSession } from "../lib/auth-client";
import { useApp } from "../store.jsx";
import { Icon } from "./Common.jsx";

// Two-factor authentication (feature #19). Enrollment: verify password →
// receive TOTP URI + backup codes → scan QR in an authenticator app → confirm
// with a 6-digit code. Disabling requires the password again.
export default function TwoFactorSettings() {
  const { toast } = useApp();
  const { data: session, refetch } = useSession();
  const enabled = !!session?.user?.twoFactorEnabled;

  const [step, setStep] = useState("idle"); // idle | password | verify
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("idle"); setPassword(""); setCode("");
    setTotpUri(""); setBackupCodes([]); setBusy(false);
  };

  const beginEnable = async () => {
    setBusy(true);
    try {
      const { data, error } = await twoFactor.enable({ password });
      if (error) throw new Error(error.message || "Wrong password");
      setTotpUri(data?.totpURI || "");
      setBackupCodes(data?.backupCodes || []);
      setStep("verify");
    } catch (e) {
      toast(e.message || "Could not start 2FA setup", "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const { error } = await twoFactor.verifyTotp({ code: code.trim() });
      if (error) throw new Error(error.message || "Invalid code");
      toast("Two-factor authentication enabled", "success");
      reset();
      refetch?.();
    } catch (e) {
      toast(e.message || "Invalid code — try again", "error");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const { error } = await twoFactor.disable({ password });
      if (error) throw new Error(error.message || "Wrong password");
      toast("Two-factor authentication disabled", "success");
      reset();
      refetch?.();
    } catch (e) {
      toast(e.message || "Could not disable 2FA", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: "grid", placeItems: "center", background: enabled ? "rgba(16,185,129,.15)" : "rgba(148,163,184,.12)", color: enabled ? "#10B981" : "var(--text-muted)" }}>
            <Icon name="shield-check" size={20} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
              Two-Factor Authentication
              <span className={`badge ${enabled ? "badge-success" : "badge-muted"}`}>
                <span className="badge-dot" /> {enabled ? "On" : "Off"}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3, maxWidth: 460 }}>
              Add a second step at sign-in using an authenticator app (Google Authenticator, Authy, 1Password).
            </div>
          </div>
        </div>
        {step === "idle" && (
          enabled ? (
            <button className="btn btn-ghost" onClick={() => setStep("password")}>Disable</button>
          ) : (
            <button className="btn btn-primary" onClick={() => setStep("password")}>
              <Icon name="shield-plus" size={14} /> Enable 2FA
            </button>
          )
        )}
      </div>

      {/* Password step (for both enable + disable) */}
      {step === "password" && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
          <div className="field" style={{ maxWidth: 340 }}>
            <label>Confirm your password</label>
            <input className="input" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} autoFocus />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={reset}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={busy || !password}
              onClick={enabled ? disable : beginEnable}>
              {busy ? "Please wait…" : enabled ? "Disable 2FA" : "Continue"}
            </button>
          </div>
        </div>
      )}

      {/* Verify step (enable) */}
      {step === "verify" && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ background: "#fff", padding: 12, borderRadius: 12, display: "inline-block" }}>
              {totpUri ? <QRCodeSVG value={totpUri} size={148} /> : null}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 8, maxWidth: 170 }}>
              Scan with your authenticator app
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="field">
              <label>Enter the 6-digit code</label>
              <input className="input" inputMode="numeric" maxLength={6} value={code}
                placeholder="123456"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus />
            </div>
            {backupCodes.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Backup codes <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>— save these somewhere safe</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontFamily: "monospace", fontSize: 12.5, background: "var(--surface-2, rgba(148,163,184,.08))", padding: 10, borderRadius: 8 }}>
                  {backupCodes.map((b) => <span key={b}>{b}</span>)}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={reset}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={busy || code.length !== 6} onClick={confirmEnable}>
                {busy ? "Verifying…" : "Verify & enable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
